import React, { useState, useCallback } from 'react';
import { useTelegram } from './TelegramProvider';
import { useTon } from './TelegramTonProvider';
import { useGameBalance } from './hooks/useGameBalance';
import { useMultiplier } from './hooks/useMultiplier';
import { useSounds } from './hooks/useSounds';
import { formatLilape } from './config/gameConfig';
import { DepositWithdrawPanel } from './components/DepositWithdrawPanel';

const ROWS = 8;
const WIN_RATE = 0.03; // 3% win rate
// Multipliers: edges are wins, center is losses (x10 base, x30 with Gold VIP)
const MULTS = [10, 2, 0, 0, 0, 0, 0, 2, 10];
const MULT_COLORS = ['#A855F7', '#7C3AED', '#EF4444', '#EF4444', '#EF4444', '#EF4444', '#EF4444', '#7C3AED', '#A855F7'];
const BET_OPTIONS = [20_000_000, 50_000_000, 100_000_000, 500_000_000];

function pathFromBits(bits, forceSlot = null) {
  let pos = 0;
  const path = [];

  if (forceSlot !== null) {
    // Force path to specific slot
    const targetPos = forceSlot;
    let currentPos = 0;
    for (let i = 0; i < ROWS; i++) {
      const remaining = ROWS - i - 1;
      const minReachable = currentPos;
      const maxReachable = currentPos + remaining + 1;

      let goRight;
      if (targetPos <= currentPos) {
        goRight = 0;
      } else if (targetPos >= currentPos + remaining + 1) {
        goRight = 1;
      } else {
        goRight = Math.random() < 0.5 ? 1 : 0;
        if (goRight) currentPos++;
      }

      path.push(goRight);
      if (goRight) currentPos++;
    }
    return { path, slot: currentPos };
  }

  for (let i = 0; i < ROWS; i++) {
    const b = (bits >> i) & 1;
    path.push(b);
    pos += b;
  }
  return { path, slot: pos };
}

export function PlinkoGame({ onBack }) {
  const { hapticFeedback } = useTelegram();
  const { connected, sendTransaction, address } = useTon();
  const gameBalanceHook = useGameBalance(address, sendTransaction);
  const { gameBalance, useSpin, addWinnings, canSpin } = gameBalanceHook;
  const { applyMultiplier } = useMultiplier();
  const { play, playTone } = useSounds();

  const [bet, setBet] = useState(BET_OPTIONS[0]);
  const [dropping, setDropping] = useState(false);
  const [ballPos, setBallPos] = useState(null);
  const [showWallet, setShowWallet] = useState(false);
  const [landSlot, setLandSlot] = useState(null);
  const [outcome, setOutcome] = useState(null);
  const [winAmt, setWinAmt] = useState(0);
  const [activePath, setActivePath] = useState(null);

  const animateDrop = useCallback((path, slot, onDone) => {
    setActivePath(path);
    let row = -1;
    let col = 4;
    const iv = setInterval(() => {
      row++;
      if (row < ROWS) {
        col += path[row] === 1 ? 0.5 : -0.5;
        setBallPos({ row, col: Math.max(0, Math.min(ROWS, col)) });
        playTone(300 + row * 50, 0.03, 'sine');
        hapticFeedback?.('light');
      } else {
        clearInterval(iv);
        setLandSlot(slot);
        setBallPos(null);
        onDone();
      }
    }, 200);
  }, [hapticFeedback, playTone]);

  const dropBall = useCallback(async () => {
    if (dropping) return;
    if (!canSpin(bet)) {
      hapticFeedback?.('error');
      return;
    }
    const betSuccess = await useSpin(bet, 'plinko');
    if (!betSuccess) return;

    hapticFeedback?.('medium');
    setDropping(true);
    setOutcome(null);
    setLandSlot(null);

    // 3% win rate - force ball to winning slot or losing center
    const won = Math.random() < WIN_RATE;
    let path, slot;
    if (won) {
      // Win - force to edge (slot 0 or 8 for 30x)
      const forceSlot = Math.random() < 0.5 ? 0 : 8;
      ({ path, slot } = pathFromBits(0, forceSlot));
    } else {
      // Lose - force to center (slots 2-6 for 0x)
      const forceSlot = 3 + Math.floor(Math.random() * 3); // 3, 4, or 5
      ({ path, slot } = pathFromBits(0, forceSlot));
    }

    animateDrop(path, slot, () => {
      const mult = MULTS[slot];
      const baseGain = Math.round(bet * mult);
      const gain = applyMultiplier(baseGain);
      addWinnings(gain, 'plinko', bet);
      setWinAmt(gain);
      setOutcome(gain >= bet ? 'win' : 'lose');
      play(gain >= bet * 2 ? 'bigWin' : gain >= bet ? 'win' : 'lose');
      setDropping(false);
      hapticFeedback?.(gain >= bet ? 'heavy' : 'error');
    });
  }, [dropping, bet, canSpin, useSpin, addWinnings, applyMultiplier, hapticFeedback, animateDrop, play]);

  const pegRows = Array.from({ length: ROWS }, (_, r) =>
    Array.from({ length: r + 2 }, (_, c) => ({ r, c }))
  );

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #1a1a2e 0%, #0f0f1a 100%)',
      padding: '16px',
      paddingBottom: '100px',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
        <button onClick={onBack} style={{
          background: 'rgba(255,255,255,0.1)',
          border: 'none',
          borderRadius: '12px',
          padding: '10px 16px',
          color: '#fff',
          fontSize: '16px',
          cursor: 'pointer',
        }}>← Back</button>
        <h1 style={{
          flex: 1,
          textAlign: 'center',
          color: '#10B981',
          fontSize: '24px',
          margin: 0,
        }}>📍 Plinko</h1>
      </div>

      {/* Balance */}
      <div style={{
        background: 'rgba(16,185,129,0.1)',
        border: '1px solid rgba(16,185,129,0.3)',
        borderRadius: '16px',
        padding: '16px',
        textAlign: 'center',
        marginBottom: '20px',
      }}>
        <div style={{ color: '#888', fontSize: '12px' }}>GAME BALANCE</div>
        <div style={{ color: '#10B981', fontSize: '28px', fontWeight: 'bold', marginBottom: 8 }}>
          {formatLilape(gameBalance)} LILAPE
        </div>
        <button onClick={() => setShowWallet(true)} style={{
          padding: '8px 20px', borderRadius: 8,
          background: 'linear-gradient(135deg, #10B981, #059669)',
          border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
        }}>💰 Deposit / Withdraw</button>
      </div>

      {showWallet && <DepositWithdrawPanel onClose={() => setShowWallet(false)} gameBalanceHook={gameBalanceHook} />}

      {/* Plinko Board */}
      <div style={{
        position: 'relative',
        padding: '20px',
        background: 'rgba(255,255,255,0.03)',
        borderRadius: '16px',
        marginBottom: '20px',
      }}>
        {/* Pegs */}
        {pegRows.map((row, rowIdx) => (
          <div key={rowIdx} style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '20px',
            marginBottom: '8px',
          }}>
            {row.map((_, pegIdx) => (
              <div key={pegIdx} style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.3)',
              }} />
            ))}
          </div>
        ))}

        {/* Ball */}
        {ballPos && (
          <div style={{
            position: 'absolute',
            left: `calc(50% + ${(ballPos.col - 4) * 24}px - 8px)`,
            top: `${20 + ballPos.row * 18}px`,
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #FFD700, #FFA500)',
            boxShadow: '0 0 12px rgba(255,215,0,0.6)',
            transition: 'left 0.15s, top 0.15s',
          }} />
        )}

        {/* Slots */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '4px',
          marginTop: '16px',
        }}>
          {MULTS.map((m, i) => (
            <div key={i} style={{
              width: '32px',
              height: '40px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
              fontWeight: 'bold',
              background: landSlot === i ? MULT_COLORS[i] : `${MULT_COLORS[i]}33`,
              color: landSlot === i ? '#fff' : MULT_COLORS[i],
              border: `1px solid ${MULT_COLORS[i]}`,
              transition: 'all 0.3s',
            }}>
              {m}×
            </div>
          ))}
        </div>
      </div>

      {/* Result */}
      {outcome && (
        <div style={{
          textAlign: 'center',
          marginBottom: '20px',
          padding: '16px',
          borderRadius: '12px',
          background: outcome === 'win' ? 'rgba(0,255,0,0.1)' : 'rgba(255,0,0,0.1)',
        }}>
          <div style={{
            fontSize: '24px',
            fontWeight: 'bold',
            color: outcome === 'win' ? '#00ff00' : '#ff4444',
          }}>
            {outcome === 'win' ? `🎉 WIN +${formatLilape(winAmt)}` : `😢 ${formatLilape(winAmt)}`}
          </div>
          <div style={{ color: '#888', fontSize: '12px', marginTop: '4px' }}>
            Multiplier: {MULTS[landSlot]}×
          </div>
        </div>
      )}

      {/* Bet Options */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ color: '#888', fontSize: '12px', marginBottom: '8px', textAlign: 'center' }}>
          BET AMOUNT
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
          {BET_OPTIONS.map(opt => (
            <button
              key={opt}
              onClick={() => !dropping && setBet(opt)}
              style={{
                padding: '10px 16px',
                borderRadius: '8px',
                border: bet === opt ? '2px solid #10B981' : '1px solid rgba(255,255,255,0.2)',
                background: bet === opt ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.05)',
                color: bet === opt ? '#10B981' : '#fff',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: 'pointer',
                opacity: dropping ? 0.5 : 1,
              }}
            >{formatLilape(opt)}</button>
          ))}
        </div>
      </div>

      {/* Drop Button */}
      <button
        onClick={dropBall}
        disabled={dropping || !canSpin(bet)}
        style={{
          width: '100%',
          padding: '18px',
          borderRadius: '14px',
          border: 'none',
          background: dropping ? '#555' : canSpin(bet) ? 'linear-gradient(135deg, #10B981, #059669)' : '#333',
          color: '#fff',
          fontSize: '18px',
          fontWeight: 'bold',
          cursor: dropping || !canSpin(bet) ? 'not-allowed' : 'pointer',
        }}
      >
        {dropping ? '📍 DROPPING...' : `DROP ${formatLilape(bet)}`}
      </button>
    </div>
  );
}
